import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { DatePicker, Popover, Select, ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { getGroupSettlementStats, getGroupInOutCashList, getCashbookList } from '../services/apiService';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import GroupSettlementDetailModal from '../components/GroupSettlementDetailModal';
import { getGroupManagerChecks } from '../services/groupManagerCheckApi';

dayjs.locale('ko');

const formatPaymentDate = (v) => {
  if (!v) return '-';
  const s = String(v);
  if (s.length >= 10) return s.slice(0, 10);
  return s;
};

const formatAccountInfo = (cashRow) => {
  if (!cashRow) return '';
  const bank = String(cashRow?.종류 || '').trim();
  const account = String(cashRow?.계좌카드번호 || cashRow?.계좌번호 || '').trim();
  return [bank, account].filter(Boolean).join(' / ');
};

const formatCashRemark = (row) => {
  if (!row) return '';
  const date = formatPaymentDate(row?.결제일);
  const bank = String(row?.결제기관명 || '').trim();
  const account = String(row?.통장정보 || '').trim();
  const note1 = String(row?.결제비고1 || '').trim();
  const note2 = String(row?.결제비고2 || '').trim();
  const notes = [note1, note2].filter(Boolean).join(', ');
  return [date !== '-' ? date : '', bank, account, notes].filter(Boolean).join(' ').trim();
};

const isDepositType = (구분) => {
  const s = String(구분 || '').trim();
  return s === '입금' || s.includes('입금');
};

const isWithdrawalType = (구분) => {
  const s = String(구분 || '').trim();
  return s === '출금' || s.includes('출금');
};

/** 입출금 SP에 통장필드가 없어 출납리스트(매핑)로 계좌정보를 붙인다. */
const enrichInOutRowsWithAccount = (inOutList, cashbook) => {
  const list = Array.isArray(cashbook?.list) ? cashbook.list : [];
  const mapping = Array.isArray(cashbook?.mapping) ? cashbook.mapping : [];
  const byTx = new Map();
  list.forEach((r) => {
    const key = String(r?.거래일련번호 ?? '').trim();
    if (key) byTx.set(key, r);
  });

  const byPreDeposit = new Map();
  const byGroupAmount = new Map();
  mapping.forEach((m) => {
    const cash = byTx.get(String(m?.거래일련번호 ?? '').trim());
    if (!cash) return;
    const preId = Number(m?.전도금번호);
    if (Number.isFinite(preId) && preId > 0) byPreDeposit.set(preId, cash);
    const groupNo = Number(m?.행사번호);
    const amount = Number(m?.출금액);
    if (Number.isFinite(groupNo) && groupNo > 0 && Number.isFinite(amount)) {
      const key = `${groupNo}|${amount}`;
      if (!byGroupAmount.has(key)) byGroupAmount.set(key, []);
      byGroupAmount.get(key).push(cash);
    }
  });

  return (Array.isArray(inOutList) ? inOutList : []).map((row) => {
    const ref = String(row?.참조번호 ?? '').trim();
    let cash = ref ? byTx.get(ref) : null;
    if (!cash) {
      const preId = Number(row?.참조번호);
      if (Number.isFinite(preId) && preId > 0) cash = byPreDeposit.get(preId) || null;
    }
    if (!cash && isWithdrawalType(row?.구분)) {
      const groupNo = Number(row?.행사단체번호);
      const amount = Number(row?.결제금액);
      const candidates = byGroupAmount.get(`${groupNo}|${amount}`) || [];
      cash = candidates[0] || null;
    }
    const info = formatAccountInfo(cash);
    if (!info) return row;
    return {
      ...row,
      통장정보: info,
      통장종류: cash?.종류 || '',
      계좌카드번호: cash?.계좌카드번호 || cash?.계좌번호 || '',
    };
  });
};

const formatNumber = (v) => {
  if (v === null || v === undefined || v === '') return '0';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  if (isNaN(n)) return '0';
  return n.toLocaleString('ko-KR');
};

const parseNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

const stripMarkup = (v) => {
  if (!v) return '';
  return String(v).replace(/<[^>]*>/g, '').trim();
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const GROUP_TABLE_COL_KEYS = [
  '행사단체번호',
  '행사단체명',
  '대리점',
  '수배담당자',
  '시작일',
  '종료일',
  '판매건수',
  '행사인원',
  '판매NET합계',
  '입금액합계',
  '미수액합계',
  '대리점수수료합계',
  '통장출금합계',
  '계산최종수익',
  '마감상태명',
  '담당자확인',
];

const DEFAULT_GROUP_COL_WIDTHS = {
  행사단체번호: 110,
  행사단체명: 200,
  대리점: 140,
  수배담당자: 100,
  시작일: 100,
  종료일: 100,
  판매건수: 72,
  행사인원: 72,
  판매NET합계: 100,
  입금액합계: 100,
  미수액합계: 100,
  대리점수수료합계: 110,
  통장출금합계: 110,
  계산최종수익: 100,
  마감상태명: 90,
  담당자확인: 90,
};

const GROUP_COL_MIN_WIDTH = 48;
const GROUP_NAME_CELL_PADDING = 16; // px-2 left+right
const GROUP_TABLE_SESSION_KEY = 'groupSettlement.tableLayout.v1';

const normalizeGroupColOrder = (order) => {
  const valid = new Set(GROUP_TABLE_COL_KEYS);
  const seen = new Set();
  const next = [];
  (Array.isArray(order) ? order : []).forEach((k) => {
    if (valid.has(k) && !seen.has(k)) {
      next.push(k);
      seen.add(k);
    }
  });
  GROUP_TABLE_COL_KEYS.forEach((k) => {
    if (!seen.has(k)) next.push(k);
  });
  return next;
};

const loadGroupTableLayout = () => {
  try {
    const raw = sessionStorage.getItem(GROUP_TABLE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveGroupTableLayout = (order, widths) => {
  try {
    sessionStorage.setItem(
      GROUP_TABLE_SESSION_KEY,
      JSON.stringify({ order, widths })
    );
  } catch {
    // ignore quota / private mode errors
  }
};

const measureTextPx = (text, font = '500 12px Pretendard, "Noto Sans KR", sans-serif') => {
  if (typeof document === 'undefined') return String(text || '').length * 8;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return String(text || '').length * 8;
  ctx.font = font;
  return ctx.measureText(String(text || '')).width;
};

const ResizableTh = ({
  colKey,
  className = '',
  children,
  onResizeStart,
  onColDragStart,
  onColDragOver,
  onColDrop,
  onColDragEnd,
  isDragOver,
  isDragging,
}) => (
  <th
    className={`relative ${className} ${isDragOver ? 'bg-blue-100 ring-1 ring-inset ring-blue-400' : ''} ${isDragging ? 'opacity-40' : ''}`}
    draggable
    onDragStart={(e) => onColDragStart?.(e, colKey)}
    onDragOver={(e) => onColDragOver?.(e, colKey)}
    onDrop={(e) => onColDrop?.(e, colKey)}
    onDragEnd={onColDragEnd}
    title="드래그하여 열 위치 변경"
  >
    {children}
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`${colKey} 열 너비 조절`}
      title="드래그하여 열 너비 조절"
      draggable={false}
      onMouseDown={(e) => {
        e.stopPropagation();
        onResizeStart(e, colKey);
      }}
      onDragStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize select-none hover:bg-blue-400/50 active:bg-blue-500/60"
    />
  </th>
);

const GroupSettlement = () => {
  const today = new Date();
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);

  const toYmd = (d) => (d ? d.format('YYYY-MM-DD') : '');
  const toDayjs = (s) => (s ? dayjs(s, 'YYYY-MM-DD') : null);

  const [startDate, setStartDate] = useState(formatDate(today));
  const [endDate, setEndDate] = useState(formatDate(oneMonthLater));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupRows, setGroupRows] = useState([]);
  const [individualRows, setIndividualRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('group');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Group 테이블 헤더(소팅/필터) UI
  const [groupHeaderFilterKey, setGroupHeaderFilterKey] = useState(null); // open popover field
  const [groupHeaderFilterSelected, setGroupHeaderFilterSelected] = useState({}); // { field: string[] }
  const [groupHeaderFilterSearch, setGroupHeaderFilterSearch] = useState({}); // { field: string }

  // Group Info Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSaleNo, setDrawerSaleNo] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerSettlementAutoOpen, setDrawerSettlementAutoOpen] = useState(false);

  // Group detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalGroup, setDetailModalGroup] = useState(null);

  // 통장입출금 리스트 (행사단체번호 매핑)
  const [groupInOutRows, setGroupInOutRows] = useState([]);
  // 담당자 정산 확인 (Supabase) — Map<단체번호(number), { 확인여부, 확인자, 확인일시 }>
  const [managerCheckMap, setManagerCheckMap] = useState(() => new Map());
  const [cashDetailModal, setCashDetailModal] = useState({
    open: false,
    type: '입금',
    groupNo: null,
    groupName: '',
    rows: [],
  });

  // 단체 테이블 컬럼 너비/순서 (드래그 리사이즈·이동, sessionStorage 유지)
  const [groupColWidths, setGroupColWidths] = useState(() => {
    const saved = loadGroupTableLayout();
    return { ...DEFAULT_GROUP_COL_WIDTHS, ...(saved?.widths || {}) };
  });
  const [groupColOrder, setGroupColOrder] = useState(() => {
    const saved = loadGroupTableLayout();
    return normalizeGroupColOrder(saved?.order);
  });
  const [dragColKey, setDragColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);
  const colResizeRef = useRef(null);

  const openGroupDrawer = (group) => {
    const firstSale = group.sales && group.sales.length > 0 ? group.sales[0] : null;
    const saleNo = firstSale?.판매번호 || null;
    if (!saleNo) return;
    setDrawerSettlementAutoOpen(false);
    setDrawerSaleNo(saleNo);
    setDrawerTitle(group.행사단체명 || '단체정보');
    setDrawerOpen(true);
  };

  const openSettlementFromDetail = (group) => {
    const firstSale = group?.sales && group.sales.length > 0 ? group.sales[0] : null;
    const saleNo = firstSale?.판매번호 || null;
    if (!saleNo) return;
    setDetailModalOpen(false);
    setDrawerSettlementAutoOpen(true);
    setDrawerSaleNo(saleNo);
    setDrawerTitle(group.행사단체명 || '단체정보');
    setDrawerOpen(true);
  };

  const closeGroupDrawer = () => {
    setDrawerOpen(false);
    setDrawerSettlementAutoOpen(false);
  };

  const openReservationDetail = (sale) => {
    const orderNo = sale?.주문번호;
    const saleNo = sale?.판매번호;
    if (!orderNo) return;
    window.open(
      `/reservation-detail/${orderNo}?saleNo=${saleNo}&productName=${encodeURIComponent(sale?.행사단체명 || '')}`,
      '_blank'
    );
  };

  const openDetailModal = (group) => {
    if (!group) return;
    setDetailModalGroup(group);
    setDetailModalOpen(true);
  };

  const openCashDetailModal = (group, type) => {
    if (!group) return;
    const rows = type === '입금'
      ? (group.통장입금목록 || [])
      : (group.통장출금목록 || []);
    setCashDetailModal({
      open: true,
      type,
      groupNo: group.행사단체번호,
      groupName: group.행사단체명 || '',
      rows,
    });
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, inOutList, cashbook] = await Promise.all([
        getGroupSettlementStats({ startDate, endDate }),
        getGroupInOutCashList({ queryDate: endDate }).catch((e) => {
          try { console.warn('[GroupSettlement] 입출금 리스트 조회 실패', e); } catch (_) {}
          return [];
        }),
        getCashbookList({ endDate }).catch((e) => {
          try { console.warn('[GroupSettlement] 출납 리스트 조회 실패', e); } catch (_) {}
          return { list: [], mapping: [] };
        }),
      ]);
      const nextGroupRows = (result && result.groupRows) || (Array.isArray(result) ? result : []);
      // 담당자 확인은 부가 정보라 기다리지 않는다. 실패해도 모두 "미확인" 으로 보인다.
      getGroupManagerChecks(nextGroupRows.map((r) => r?.행사단체번호))
        .then(setManagerCheckMap)
        .catch(() => setManagerCheckMap(new Map()));
      if (result && result.groupRows) {
        setGroupRows(result.groupRows);
        setIndividualRows(result.individualRows || []);
      } else if (Array.isArray(result)) {
        setGroupRows(result);
        setIndividualRows([]);
      } else {
        setGroupRows([]);
        setIndividualRows([]);
      }
      const rawInOut = Array.isArray(inOutList) ? inOutList : [];
      setGroupInOutRows(enrichInOutRowsWithAccount(rawInOut, cashbook));
      setSearched(true);
    } catch (err) {
      setError(err?.message || '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const setQuickPeriod = (type) => {
    const t = new Date();
    if (type === 'today') {
      setStartDate(formatDate(t));
      setEndDate(formatDate(t));
    } else if (type === 'week') {
      const end = new Date(t);
      end.setDate(t.getDate() + 7);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    } else if (type === 'month') {
      const end = new Date(t);
      end.setMonth(t.getMonth() + 1);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    } else if (type === '3months') {
      const end = new Date(t);
      end.setMonth(t.getMonth() + 3);
      setStartDate(formatDate(t));
      setEndDate(formatDate(end));
    }
  };

  // Group rows by 행사단체번호
  const groupedData = useMemo(() => {
    const inOutByGroup = new Map();
    (groupInOutRows || []).forEach((row) => {
      const key = Number(row?.행사단체번호);
      if (!key) return;
      if (!inOutByGroup.has(key)) {
        inOutByGroup.set(key, { deposits: [], withdrawals: [] });
      }
      const bucket = inOutByGroup.get(key);
      if (isDepositType(row?.구분)) bucket.deposits.push(row);
      else if (isWithdrawalType(row?.구분)) bucket.withdrawals.push(row);
    });

    const map = new Map();
    groupRows.forEach((row) => {
      const key = row.행사단체번호;
      if (!map.has(key)) {
        map.set(key, {
          행사단체번호: key,
          행사단체명: row.행사단체명 || '',
          시작일: row.시작일 || '',
          종료일: row.종료일 || '',
          정산기준일: row.정산기준일 || '',
          판매건수: parseNumber(row.판매건수),
          행사인원: row.행사인원 || '0',
          행사인원2: parseNumber(row.행사인원2),
          취소건수: parseNumber(row.취소건수),
          취소인원: row.취소인원 || '0/0/0',
          인솔자: row.인솔자 || '',
          수배담당자: row.수배담당자 || '',
          수배담당부서: row.수배담당부서 || '',
          대리점: '',
          원화환산액: parseNumber(row.원화환산액),
          출금액: parseNumber(row.출금액),
          미지급액: parseNumber(row.미지급액),
          알선수익: parseNumber(row.알선수익),
          대매수익: parseNumber(row.대매수익),
          기타수익: parseNumber(row.기타수익),
          수익합계: parseNumber(row.수익합계),
          최종수익: parseNumber(row.최종수익),
          마감상태: row.마감상태 || '',
          마감상태코드: row.마감상태코드 || '',
          마감상태명: row.마감상태명 || '',
          처리일시: row.처리일시 || '',
          처리자: row.처리자 || '',
          sales: [],
          판매NET합계: 0,
          입금액합계: 0,
          미수액합계: 0,
          대리점수수료합계: 0,
          계산최종수익: 0,
          통장입금목록: [],
          통장출금목록: [],
          통장입금합계: 0,
          통장출금합계: 0,
        });
      }
      map.get(key).sales.push(row);
    });
    const groups = Array.from(map.values());
    groups.forEach((g) => {
      let netSum = 0;
      let depositSum = 0;
      let commissionSum = 0;
      const agencies = new Set();
      (g.sales || []).forEach((sale) => {
        const n = parseNumber(sale.판매현황NET);
        const d = parseNumber(sale.판매현황입금액);
        netSum += n;
        depositSum += d;
        commissionSum += parseNumber(sale.대리점수수료);
        const agency = String(sale.대리점 || '').trim();
        if (agency) agencies.add(agency);
      });
      g.판매NET합계 = netSum;
      g.입금액합계 = depositSum;
      g.미수액합계 = netSum - depositSum;
      g.대리점수수료합계 = commissionSum;
      g.대리점 = Array.from(agencies).join(', ');
      g.계산최종수익 = g.최종수익 - g.미수액합계;

      const cash = inOutByGroup.get(Number(g.행사단체번호)) || { deposits: [], withdrawals: [] };
      g.통장입금목록 = cash.deposits;
      g.통장출금목록 = cash.withdrawals;
      g.통장입금합계 = cash.deposits.reduce((sum, r) => sum + parseNumber(r?.결제금액), 0);
      g.통장출금합계 = cash.withdrawals.reduce((sum, r) => sum + parseNumber(r?.결제금액), 0);

      const check = managerCheckMap.get(Number(g.행사단체번호));
      g.담당자확인 = check?.확인여부 ? '확인' : '미확인';
      g.담당자확인자 = check?.확인여부 ? (check?.확인자 || '') : '';
    });
    return groups;
  }, [groupRows, groupInOutRows, managerCheckMap]);

  const groupHeaderFilterFields = useMemo(
    () => [
      '행사단체번호',
      '행사단체명',
      '대리점',
      '수배담당자',
      '시작일',
      '종료일',
      '통장출금합계',
      '계산최종수익',
      '마감상태명',
      '담당자확인',
    ],
    []
  );

  const groupHeaderFilterOptions = useMemo(() => {
    const map = {};
    groupHeaderFilterFields.forEach((f) => { map[f] = []; });
    groupHeaderFilterFields.forEach((f) => {
      const set = new Set();
      (groupedData || []).forEach((g) => {
        const raw = g?.[f];
        if (raw === null || raw === undefined || raw === '') return;
        set.add(String(raw));
      });
      const arr = Array.from(set);
      arr.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      map[f] = arr;
    });
    return map;
  }, [groupHeaderFilterFields, groupedData]);

  const renderGroupHeaderFilterContent = (field) => {
    const options = groupHeaderFilterOptions?.[field] || [];
    const selected = Array.isArray(groupHeaderFilterSelected?.[field]) ? groupHeaderFilterSelected?.[field] : [];
    const searchRaw = String(groupHeaderFilterSearch?.[field] ?? '');
    const q = searchRaw.trim().toLowerCase();
    const filteredOptions = options.filter((v) => {
      if (!q) return true;
      return String(v).toLowerCase().includes(q);
    });

    const isAllSelected = options.length > 0 && options.every((v) => selected.includes(v));

    const toggleAll = () => {
      setGroupHeaderFilterSelected((prev) => ({
        ...prev,
        [field]: isAllSelected ? [] : [...options],
      }));
    };

    const toggleOption = (v) => {
      setGroupHeaderFilterSelected((prev) => {
        const cur = Array.isArray(prev?.[field]) ? prev?.[field] : [];
        const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
        return { ...prev, [field]: next };
      });
    };

    return (
      <div
        className="p-2 w-56"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-gray-100">
          <input
            type="text"
            placeholder="검색..."
            value={searchRaw}
            onChange={(e) => setGroupHeaderFilterSearch((prev) => ({ ...prev, [field]: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
          />
        </div>
        <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
          <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
            <input type="checkbox" checked={isAllSelected} onChange={toggleAll} />
            <span>(모두 선택)</span>
          </label>
          <div className="my-1 border-t border-gray-100" />
          {filteredOptions.map((v) => (
            <label
              key={String(v)}
              className="flex items-center gap-2 py-1 cursor-pointer select-none"
            >
              <input type="checkbox" checked={selected.includes(v)} onChange={() => toggleOption(v)} />
              <span className="truncate">{String(v)}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  // Filter
  const filteredGroups = useMemo(() => {
    let result = groupedData;
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      result = result.filter((g) =>
        String(g.행사단체명).toLowerCase().includes(kw) ||
        String(g.행사단체번호).toLowerCase().includes(kw) ||
        String(g.인솔자).toLowerCase().includes(kw) ||
        String(g.수배담당자).toLowerCase().includes(kw) ||
        g.sales.some((s) => String(s.대리점 || '').toLowerCase().includes(kw))
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((g) => {
        if (statusFilter === 'closed') return g.마감상태코드 === '1';
        if (statusFilter === 'open') return g.마감상태코드 === '0';
        return true;
      });
    }

    const activeHeaderFilters = Object.entries(groupHeaderFilterSelected || {}).filter(
      ([, vals]) => Array.isArray(vals) && vals.length > 0
    );
    if (activeHeaderFilters.length > 0) {
      result = result.filter((g) =>
        activeHeaderFilters.every(([field, selectedVals]) =>
          selectedVals.includes(String(g?.[field] ?? ''))
        )
      );
    }
    return result;
  }, [groupedData, keyword, statusFilter, groupHeaderFilterSelected]);

  // Sort
  const sortedGroups = useMemo(() => {
    if (!sortKey) return filteredGroups;
    const sorted = [...filteredGroups];
    sorted.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === '최종수익') {
        va = a.계산최종수익;
        vb = b.계산최종수익;
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '');
      vb = String(vb || '');
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return sorted;
  }, [filteredGroups, sortKey, sortDir]);

  // 단체명 컬럼 최대 너비 = 현재 목록에서 가장 긴 텍스트 폭 (+ 셀 패딩)
  const groupNameMaxWidth = useMemo(() => {
    let maxPx = measureTextPx('단체명') + 72; // 헤더 정렬/필터 버튼 여유
    sortedGroups.forEach((g) => {
      const w = measureTextPx(g?.행사단체명 || '');
      if (w > maxPx) maxPx = w;
    });
    return Math.max(GROUP_COL_MIN_WIDTH, Math.ceil(maxPx + GROUP_NAME_CELL_PADDING));
  }, [sortedGroups]);

  useEffect(() => {
    setGroupColWidths((prev) => {
      const cur = prev.행사단체명 ?? DEFAULT_GROUP_COL_WIDTHS.행사단체명;
      if (cur <= groupNameMaxWidth) return prev;
      return { ...prev, 행사단체명: groupNameMaxWidth };
    });
  }, [groupNameMaxWidth]);

  const startGroupColResize = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    colResizeRef.current = {
      key: colKey,
      startX: e.clientX,
      startWidth: groupColWidths[colKey] ?? DEFAULT_GROUP_COL_WIDTHS[colKey] ?? GROUP_COL_MIN_WIDTH,
      maxWidth: colKey === '행사단체명' ? groupNameMaxWidth : null,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [groupColWidths, groupNameMaxWidth]);

  useEffect(() => {
    const onMove = (e) => {
      const r = colResizeRef.current;
      if (!r) return;
      let next = Math.max(GROUP_COL_MIN_WIDTH, r.startWidth + (e.clientX - r.startX));
      if (r.maxWidth != null) next = Math.min(next, r.maxWidth);
      setGroupColWidths((prev) => (prev[r.key] === next ? prev : { ...prev, [r.key]: next }));
    };
    const onUp = () => {
      if (!colResizeRef.current) return;
      colResizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    saveGroupTableLayout(groupColOrder, groupColWidths);
  }, [groupColOrder, groupColWidths]);

  const handleColDragStart = useCallback((e, colKey) => {
    if (colResizeRef.current) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
    setDragColKey(colKey);
  }, []);

  const handleColDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColKey((prev) => (prev === colKey ? prev : colKey));
  }, []);

  const handleColDrop = useCallback((e, targetKey) => {
    e.preventDefault();
    const fromKey = e.dataTransfer.getData('text/plain') || dragColKey;
    setDragColKey(null);
    setDragOverColKey(null);
    if (!fromKey || fromKey === targetKey) return;
    setGroupColOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      return next;
    });
  }, [dragColKey]);

  const handleColDragEnd = useCallback(() => {
    setDragColKey(null);
    setDragOverColKey(null);
  }, []);

  const groupTableWidth = useMemo(
    () => groupColOrder.reduce((sum, key) => sum + (groupColWidths[key] ?? DEFAULT_GROUP_COL_WIDTHS[key] ?? GROUP_COL_MIN_WIDTH), 0),
    [groupColOrder, groupColWidths]
  );

  const filteredIndividual = useMemo(() => {
    if (!keyword.trim()) return individualRows;
    const kw = keyword.trim().toLowerCase();
    return individualRows.filter((r) =>
      String(r.판매번호 || '').toLowerCase().includes(kw) ||
      String(r.예약담당 || '').toLowerCase().includes(kw)
    );
  }, [individualRows, keyword]);

  // Summary
  const summary = useMemo(() => {
    const s = {
      totalGroups: filteredGroups.length,
      totalSales: 0,
      totalPersonnel: 0,
      totalNet: 0,
      totalDeposit: 0,
      totalCashDeposit: 0,
      totalMisu: 0,
      totalAgencyCommission: 0,
      totalAdvance: 0,
      totalDisburse: 0,
      totalUnpaid: 0,
      totalProfit: 0,
      totalFinalProfit: 0,
      closedCount: 0,
      openCount: 0,
    };
    filteredGroups.forEach((g) => {
      s.totalSales += g.판매건수;
      s.totalPersonnel += g.행사인원2;
      g.sales.forEach((sale) => {
        const net = parseNumber(sale.판매현황NET);
        const dep = parseNumber(sale.판매현황입금액);
        s.totalNet += net;
        s.totalDeposit += dep;
        s.totalMisu += net - dep;
        s.totalAgencyCommission += parseNumber(sale.대리점수수료);
      });
      s.totalCashDeposit += g.통장입금합계 || 0;
      s.totalAdvance += g.통장출금합계 || 0;
      s.totalDisburse += g.출금액;
      s.totalUnpaid += g.미지급액;
      s.totalProfit += g.수익합계;
      s.totalFinalProfit += g.계산최종수익;
      if (g.마감상태코드 === '1') s.closedCount++;
      else s.openCount++;
    });
    return s;
  }, [filteredGroups]);

  const toggleGroup = (groupNo) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupNo)) next.delete(groupNo);
      else next.add(groupNo);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(sortedGroups.map((g) => g.행사단체번호)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderGroupSortFilterHeader = (label, field, justify = 'start') => {
    const justifyCls = justify === 'end' ? 'justify-end' : justify === 'center' ? 'justify-center' : 'justify-start';
    return (
      <div className={`flex items-center ${justifyCls} gap-2`}>
        <span className="whitespace-nowrap cursor-grab active:cursor-grabbing">{label}</span>
        <button
          type="button"
          className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500"
          title="정렬"
          draggable={false}
          onClick={(e) => { e.stopPropagation(); handleSort(field); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {sortKey === field ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </button>
        <Popover
          placement="bottomLeft"
          trigger="click"
          open={groupHeaderFilterKey === field}
          onOpenChange={(o) => setGroupHeaderFilterKey(o ? field : null)}
          content={renderGroupHeaderFilterContent(field)}
        >
          <button
            type="button"
            className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.[field]) && groupHeaderFilterSelected?.[field]?.length > 0 ? 'text-blue-600' : 'text-gray-500'}`}
            title="필터"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </Popover>
      </div>
    );
  };

  const groupColHeaderProps = {
    onResizeStart: startGroupColResize,
    onColDragStart: handleColDragStart,
    onColDragOver: handleColDragOver,
    onColDrop: handleColDrop,
    onColDragEnd: handleColDragEnd,
  };

  const renderGroupHeaderCell = (colKey) => {
    const shared = {
      ...groupColHeaderProps,
      key: colKey,
      colKey,
      isDragOver: dragOverColKey === colKey,
      isDragging: dragColKey === colKey,
    };
    switch (colKey) {
      case '행사단체번호':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('단체번호', '행사단체번호')}</ResizableTh>;
      case '행사단체명':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('단체명', '행사단체명')}</ResizableTh>;
      case '대리점':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('대리점', '대리점')}</ResizableTh>;
      case '수배담당자':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('수배담당자', '수배담당자')}</ResizableTh>;
      case '시작일':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('시작일', '시작일')}</ResizableTh>;
      case '종료일':
        return <ResizableTh {...shared} className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('종료일', '종료일')}</ResizableTh>;
      case '판매건수':
        return <ResizableTh {...shared} className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">판매건수</span></ResizableTh>;
      case '행사인원':
        return <ResizableTh {...shared} className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">행사인원</span></ResizableTh>;
      case '판매NET합계':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">판매NET 합계</span></ResizableTh>;
      case '입금액합계':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">입금액 합계</span></ResizableTh>;
      case '미수액합계':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">미수액 합계</span></ResizableTh>;
      case '대리점수수료합계':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap"><span className="cursor-grab active:cursor-grabbing">대리점수수료 합계</span></ResizableTh>;
      case '통장출금합계':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('전도금(지출)', '통장출금합계', 'end')}</ResizableTh>;
      case '계산최종수익':
        return <ResizableTh {...shared} className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('최종수익', '계산최종수익', 'end')}</ResizableTh>;
      case '마감상태명':
        return <ResizableTh {...shared} className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('마감상태', '마감상태명', 'center')}</ResizableTh>;
      case '담당자확인':
        return <ResizableTh {...shared} className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{renderGroupSortFilterHeader('담당자확인', '담당자확인', 'center')}</ResizableTh>;
      default:
        return null;
    }
  };

  const renderGroupBodyCell = (colKey, group) => {
    switch (colKey) {
      case '행사단체번호':
        return (
          <td key={colKey} className="px-3 py-3 border-b border-gray-200">
            <button
              type="button"
              className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
              onClick={(e) => { e.stopPropagation(); openGroupDrawer(group); }}
              title="단체 상세 보기"
            >
              {group.행사단체번호}
            </button>
          </td>
        );
      case '행사단체명':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 font-medium text-gray-900 overflow-hidden truncate" title={group.행사단체명}>{group.행사단체명}</td>;
      case '대리점':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-gray-700 overflow-hidden truncate" title={group.대리점 || ''}>{group.대리점 || '-'}</td>;
      case '수배담당자':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-gray-700 whitespace-nowrap">{group.수배담당자 || '-'}</td>;
      case '시작일':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-gray-700">{group.시작일}</td>;
      case '종료일':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-gray-700">{group.종료일}</td>;
      case '판매건수':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-center text-gray-700">{formatNumber(group.판매건수)}</td>;
      case '행사인원':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-center text-gray-700">{group.행사인원}</td>;
      case '판매NET합계':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">{formatNumber(group.판매NET합계)}</td>;
      case '입금액합계':
        return (
          <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">
            <button
              type="button"
              className={`tabular-nums ${group.통장입금합계 > 0 ? 'text-blue-600 hover:text-blue-800 hover:underline font-medium' : 'text-gray-700 cursor-default'}`}
              title={group.통장입금합계 > 0 ? '통장 입금 내역 보기' : '매핑된 입금 내역 없음'}
              onClick={(e) => {
                e.stopPropagation();
                if (group.통장입금합계 > 0) openCashDetailModal(group, '입금');
              }}
            >
              {formatNumber(group.통장입금합계)}
            </button>
          </td>
        );
      case '미수액합계':
        return <td key={colKey} className={`px-3 py-3 border-b border-gray-200 text-right ${group.미수액합계 > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>{formatNumber(group.미수액합계)}</td>;
      case '대리점수수료합계':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">{formatNumber(group.대리점수수료합계)}</td>;
      case '통장출금합계':
        return (
          <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">
            <button
              type="button"
              className={`tabular-nums ${group.통장출금합계 > 0 ? 'text-blue-600 hover:text-blue-800 hover:underline font-medium' : 'text-gray-700 cursor-default'}`}
              title={group.통장출금합계 > 0 ? '통장 출금(전도금) 내역 보기' : '매핑된 출금 내역 없음'}
              onClick={(e) => {
                e.stopPropagation();
                if (group.통장출금합계 > 0) openCashDetailModal(group, '출금');
              }}
            >
              {formatNumber(group.통장출금합계)}
            </button>
          </td>
        );
      case '계산최종수익':
        return <td key={colKey} className={`px-3 py-3 border-b border-gray-200 text-right font-bold ${group.계산최종수익 < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatNumber(group.계산최종수익)}</td>;
      case '마감상태명':
        return <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-center">{getStatusBadge(group.마감상태코드, group.마감상태명)}</td>;
      case '담당자확인':
        return (
          <td key={colKey} className="px-3 py-3 border-b border-gray-200 text-center whitespace-nowrap" title={group.담당자확인자 ? `확인자: ${group.담당자확인자}` : ''}>
            {group.담당자확인 === '확인' ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">확인</span>
            ) : (
              <span className="text-xs text-gray-400">미확인</span>
            )}
          </td>
        );
      default:
        return null;
    }
  };

  const GROUP_FOOTER_VALUE_KEYS = useMemo(() => new Set([
    '판매건수', '행사인원', '판매NET합계', '입금액합계', '미수액합계', '대리점수수료합계', '통장출금합계', '계산최종수익',
  ]), []);

  const groupFooterLabelIdx = useMemo(() => {
    const idx = groupColOrder.findIndex((k) => !GROUP_FOOTER_VALUE_KEYS.has(k));
    return idx >= 0 ? idx : 0;
  }, [groupColOrder, GROUP_FOOTER_VALUE_KEYS]);

  const renderGroupFooterCell = (colKey, colIdx) => {
    const base = 'px-2 py-2 border-t-2 border-gray-300';
    switch (colKey) {
      case '판매건수':
        return <td key={colKey} className={`${base} text-center`}>{formatNumber(summary.totalSales)}</td>;
      case '행사인원':
        return <td key={colKey} className={`${base} text-center`}>{formatNumber(summary.totalPersonnel)}</td>;
      case '판매NET합계':
        return <td key={colKey} className={`${base} text-right`}>{formatNumber(summary.totalNet)}</td>;
      case '입금액합계':
        return <td key={colKey} className={`${base} text-right`}>{formatNumber(summary.totalCashDeposit)}</td>;
      case '미수액합계':
        return <td key={colKey} className={`${base} text-right ${summary.totalMisu > 0 ? 'text-red-600' : 'text-gray-700'}`}>{formatNumber(summary.totalMisu)}</td>;
      case '대리점수수료합계':
        return <td key={colKey} className={`${base} text-right`}>{formatNumber(summary.totalAgencyCommission)}</td>;
      case '통장출금합계':
        return <td key={colKey} className={`${base} text-right`}>{formatNumber(summary.totalAdvance)}</td>;
      case '계산최종수익':
        return <td key={colKey} className={`${base} text-right ${summary.totalFinalProfit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatNumber(summary.totalFinalProfit)}</td>;
      default:
        return <td key={colKey} className={base}>{colIdx === groupFooterLabelIdx ? '합계' : ''}</td>;
    }
  };

  const SortIcon = ({ field }) => {
    if (sortKey !== field) return <span className="text-gray-300 ml-0.5">↕</span>;
    return <span className="text-blue-500 ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const getStatusBadge = (code, name) => {
    if (code === '1') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">정산마감</span>;
    if (code === '0') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">미마감</span>;
    return <span className="text-xs text-gray-500">{name || '-'}</span>;
  };

  const getReservationBadge = (code, name) => {
    const cleaned = stripMarkup(name);
    const colorMap = {
      '049001': 'bg-blue-100 text-blue-700',
      '049002': 'bg-amber-100 text-amber-700',
      '049011': 'bg-emerald-100 text-emerald-700',
      '049019': 'bg-indigo-100 text-indigo-700',
      '049021': 'bg-red-100 text-red-700',
      '049022': 'bg-red-100 text-red-700',
      '049029': 'bg-red-100 text-red-700',
    };
    const cls = colorMap[code] || 'bg-gray-100 text-gray-600';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{cleaned || '-'}</span>;
  };

  const handleExportExcel = () => {
    try {
      if (activeTab === 'group') {
        if (!sortedGroups.length) {
          window.alert('내보낼 단체 정산 데이터가 없습니다.');
          return;
        }
        const exportRows = [];
        sortedGroups.forEach((g, index) => {
          const deposits = Array.isArray(g.통장입금목록) ? g.통장입금목록 : [];
          const withdrawals = Array.isArray(g.통장출금목록) ? g.통장출금목록 : [];
          const rowCount = Math.max(1, deposits.length, withdrawals.length);
          const statusLabel = g.마감상태코드 === '1'
            ? '정산마감'
            : g.마감상태코드 === '0'
              ? '미마감'
              : (g.마감상태명 || '');

          for (let i = 0; i < rowCount; i += 1) {
            const dep = deposits[i];
            const wit = withdrawals[i];
            const isFirst = i === 0;

            if (isFirst) {
              exportRows.push({
                'No': index + 1,
                '단체번호': g.행사단체번호 ?? '',
                '단체명': g.행사단체명 ?? '',
                '대리점': g.대리점 || '',
                '수배담당자': g.수배담당자 || '',
                '시작일': g.시작일 || '',
                '종료일': g.종료일 || '',
                '판매건수': g.판매건수 || 0,
                '행사인원': g.행사인원 || '',
                '판매NET 합계': g.판매NET합계 || 0,
                '입금액 합계': dep ? parseNumber(dep.결제금액) : (deposits.length ? '' : (g.통장입금합계 || 0)),
                '입금비고': dep ? formatCashRemark(dep) : '',
                '미수액 합계': g.미수액합계 || 0,
                '대리점수수료 합계': g.대리점수수료합계 || 0,
                '전도금(지출)': wit ? parseNumber(wit.결제금액) : (withdrawals.length ? '' : (g.통장출금합계 || 0)),
                '전도금(지출)비고': wit ? formatCashRemark(wit) : '',
                '최종수익': g.계산최종수익 || 0,
                '마감상태': statusLabel,
                '담당자확인': g.담당자확인 || '',
              });
            } else {
              exportRows.push({
                'No': '',
                '단체번호': '',
                '단체명': '',
                '대리점': '',
                '수배담당자': '',
                '시작일': '',
                '종료일': '',
                '판매건수': '',
                '행사인원': '',
                '판매NET 합계': '',
                '입금액 합계': dep ? parseNumber(dep.결제금액) : '',
                '입금비고': dep ? formatCashRemark(dep) : '',
                '미수액 합계': '',
                '대리점수수료 합계': '',
                '전도금(지출)': wit ? parseNumber(wit.결제금액) : '',
                '전도금(지출)비고': wit ? formatCashRemark(wit) : '',
                '최종수익': '',
                '마감상태': '',
                '담당자확인': '',
              });
            }
          }
        });
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '단체별 정산');
        XLSX.writeFile(workbook, `단체정산_${startDate}_${endDate}.xlsx`);
      } else {
        if (!filteredIndividual.length) {
          window.alert('내보낼 개별 판매 데이터가 없습니다.');
          return;
        }
        const exportRows = filteredIndividual.map((r, index) => ({
          'No': index + 1,
          '판매번호': r.판매번호 ?? '',
          '주문번호': r.주문번호 ?? '',
          '행사시작일': r.행사시작일 || '',
          '예약담당': r.예약담당 || '',
          '예약상태': stripMarkup(r.예약상태) || '',
          '인원': parseNumber(r.인원),
          '판매금액': parseNumber(r.판매금액),
          '항공료': parseNumber(r.항공료),
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '개별 판매');
        XLSX.writeFile(workbook, `단체정산_개별판매_${startDate}_${endDate}.xlsx`);
      }
    } catch (err) {
      try { console.error('[GroupSettlement] export excel failed', err); } catch (_) {}
      window.alert('엑셀 내보내기에 실패했습니다.');
    }
  };

  return (
    <ConfigProvider locale={koKR}>
    <div className="space-y-4 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[17px] font-bold text-gray-900">단체정산</h1>
      </div>

      {/* Search Bar */}
      <div className="rounded-2xl bg-white p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-1">시작일</label>
            <DatePicker
              className="w-full"
              value={toDayjs(startDate)}
              onChange={(d) => setStartDate(toYmd(d))}
              format="YYYY년 M월 D일"
              placeholder="년월일 선택"
              allowClear={false}
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-1">종료일</label>
            <DatePicker
              className="w-full"
              value={toDayjs(endDate)}
              onChange={(d) => setEndDate(toYmd(d))}
              format="YYYY년 M월 D일"
              placeholder="년월일 선택"
              allowClear={false}
            />
          </div>
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', key: 'today' },
              { label: '1주일', key: 'week' },
              { label: '1개월', key: 'month' },
              { label: '3개월', key: '3months' },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setQuickPeriod(p.key)}
                className="h-9 rounded-lg px-3 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            className="h-9 rounded-lg px-5 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? '조회 중...' : '조회'}
          </button>
        </div>
        {searched && (
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-200">
            <input
              type="text"
              placeholder="단체명, 단체번호, 대리점 검색..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="h-9 w-64 rounded-lg border border-gray-300 bg-white px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
            >
              <option value="all">전체 마감상태</option>
              <option value="open">미마감</option>
              <option value="closed">정산마감</option>
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 rounded-2xl p-4 text-sm text-red-600">{error}</div>
      )}

      {searched && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: '단체 수', value: summary.totalGroups, unit: '건', color: 'blue' },
              { label: '총 판매건수', value: summary.totalSales, unit: '건', color: 'indigo' },
              { label: '총 인원', value: summary.totalPersonnel, unit: '명', color: 'violet' },
              { label: '판매NET 합계', value: summary.totalNet, unit: '원', color: 'emerald' },
              { label: '최종수익 합계', value: summary.totalFinalProfit, unit: '원', color: summary.totalFinalProfit >= 0 ? 'emerald' : 'red' },
            ].map((card, idx) => (
              <div key={idx} className="rounded-2xl bg-white p-4">
                <div className="text-[13px] font-medium text-gray-500 mb-1">{card.label}</div>
                <div className={`text-lg font-bold ${card.color === 'red' ? 'text-red-600' : card.color === 'emerald' ? 'text-emerald-600' : 'text-gray-900'}`}>
                  {typeof card.value === 'number' ? formatNumber(card.value) : card.value}
                  {card.unit && <span className="text-xs font-normal text-gray-500 ml-1">{card.unit}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="inline-flex items-center gap-1 bg-gray-100 p-1 rounded-lg self-start">
            <button
              type="button"
              onClick={() => setActiveTab('group')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${activeTab === 'group' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              단체별 정산 ({sortedGroups.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('individual')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${activeTab === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              개별 판매 ({filteredIndividual.length})
            </button>
          </div>

          {/* Group Tab */}
          {activeTab === 'group' && (
            <div className="rounded-2xl bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[17px] font-bold text-gray-900">단체별 정산 내역</div>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={sortedGroups.length === 0}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  엑셀 내보내기
                </button>
              </div>
              {sortedGroups.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">조회된 단체 정산 데이터가 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="text-xs" style={{ tableLayout: 'fixed', width: groupTableWidth, minWidth: '100%' }}>
                    <colgroup>
                      {groupColOrder.map((key) => (
                        <col
                          key={key}
                          style={{ width: groupColWidths[key] ?? DEFAULT_GROUP_COL_WIDTHS[key] }}
                        />
                      ))}
                    </colgroup>
                    <thead className="sticky top-0 z-10">
                      <tr>
                        {groupColOrder.map((key) => renderGroupHeaderCell(key))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGroups.map((group, gIdx) => (
                        <tr
                          key={group.행사단체번호}
                          className="hover:bg-gray-50 transition-colors"
                          onClick={() => openDetailModal(group)}
                        >
                          {groupColOrder.map((key) => renderGroupBodyCell(key, group))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        {groupColOrder.map((key, idx) => renderGroupFooterCell(key, idx))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Individual Sales Tab */}
          {activeTab === 'individual' && (
            <div className="rounded-2xl bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[17px] font-bold text-gray-900">개별 판매 (단체 미배정)</div>
                  <div className="text-sm text-gray-500 mt-0.5">행사단체에 배정되지 않은 개별 판매 건입니다.</div>
                </div>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={filteredIndividual.length === 0}
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  엑셀 내보내기
                </button>
              </div>
              {filteredIndividual.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">조회된 개별 판매 데이터가 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">판매번호</th>
                        <th className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">행사시작일</th>
                        <th className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">예약담당</th>
                        <th className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">예약상태</th>
                        <th className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">인원</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">판매금액</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">항공료</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIndividual.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-3 border-b border-gray-200">
                            {row.판매번호 ? (
                              <button
                                type="button"
                                className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
                                onClick={() => {
                                  const orderNo = row?.주문번호;
                                  const saleNo = row?.판매번호;
                                  if (!orderNo) return;
                                  window.open(`/reservation-detail/${orderNo}?saleNo=${saleNo}`, '_blank');
                                }}
                                title="예약 상세 보기"
                              >
                                {row.판매번호}
                              </button>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-gray-700">{row.행사시작일 || '-'}</td>
                          <td className="px-3 py-3 border-b border-gray-200 text-gray-800">{row.예약담당 || '-'}</td>
                          <td className="px-3 py-3 border-b border-gray-200 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{stripMarkup(row.예약상태) || '-'}</span>
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-center text-gray-700">{formatNumber(row.인원)}</td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-800">{formatNumber(row.판매금액)}</td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">{formatNumber(row.항공료)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td className="px-3 py-2 border-t-2 border-gray-300" colSpan={4}>합계</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-center">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.인원), 0))}</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-right">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.판매금액), 0))}</td>
                        <td className="px-3 py-2 border-t-2 border-gray-300 text-right">{formatNumber(filteredIndividual.reduce((s, r) => s + parseNumber(r.항공료), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!searched && !loading && (
        <div className="rounded-2xl bg-white py-16 text-center">
          <div className="text-gray-500 text-sm">기간을 선택하고 조회 버튼을 눌러주세요.</div>
        </div>
      )}

      {loading && (
        <div className="rounded-2xl bg-white py-16 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            데이터를 불러오는 중...
          </div>
        </div>
      )}
      {/* Group Info Drawer */}
      <GroupInfoDrawer
        open={drawerOpen}
        onClose={closeGroupDrawer}
        saleNo={drawerSaleNo}
        title={drawerTitle}
        settlementAutoOpen={drawerSettlementAutoOpen}
        onUpdated={(info) => {
          if (info?.type !== 'managerCheck') return;
          setManagerCheckMap((prev) => {
            const next = new Map(prev);
            next.set(Number(info.groupNo), info.row);
            return next;
          });
        }}
      />
      <GroupSettlementDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        group={detailModalGroup}
        onOpenReservationDetail={openReservationDetail}
        onOpenSettlement={openSettlementFromDetail}
      />

      {/* 통장입출금 상세 모달 */}
      {cashDetailModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <div>
                <div className="text-[17px] font-bold text-gray-900">
                  {cashDetailModal.type === '입금' ? '입금 내역' : '전도금(지출) 출금 내역'}
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  <span className="font-medium text-gray-800 mr-2">{cashDetailModal.groupName || '-'}</span>
                  <span className="text-gray-400">/</span>
                  <span className="ml-2 text-gray-600">단체번호 {cashDetailModal.groupNo}</span>
                  <span className="ml-3 text-gray-500">
                    합계 {formatNumber((cashDetailModal.rows || []).reduce((s, r) => s + parseNumber(r?.결제금액), 0))}원
                    {' '}({(cashDetailModal.rows || []).length}건)
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCashDetailModal((prev) => ({ ...prev, open: false }))}
                className="flex size-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {(cashDetailModal.rows || []).length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-10">매핑된 내역이 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">결제일</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">은행</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">계좌번호</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">결제금액</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">결제비고1</th>
                        <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">결제비고2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cashDetailModal.rows || []).map((row, idx) => (
                        <tr
                          key={`${row?.참조번호 ?? idx}-${idx}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-700 whitespace-nowrap">
                            {formatPaymentDate(row?.결제일)}
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-700 whitespace-nowrap">
                            {row?.통장종류 || '-'}
                          </td>
                          <td
                            className="px-3 py-3 border-b border-gray-200 text-right text-gray-700 whitespace-nowrap max-w-[220px] truncate"
                            title={row?.계좌카드번호 || ''}
                          >
                            {row?.계좌카드번호 || '-'}
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right font-medium text-gray-900 tabular-nums whitespace-nowrap">
                            {formatNumber(row?.결제금액)}
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-600 max-w-[180px] truncate" title={row?.결제비고1 || ''}>
                            {row?.결제비고1 || '-'}
                          </td>
                          <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-600 max-w-[180px] truncate" title={row?.결제비고2 || ''}>
                            {row?.결제비고2 || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCashDetailModal((prev) => ({ ...prev, open: false }))}
                className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ConfigProvider>
  );
};

export default GroupSettlement;
